import type {
  BedProduct,
  CategoryAdCot,
  CategoryAdDetailed,
  CategoryAdExtraBeds,
  CategoryExtraBedCapacity,
} from "../api/contracts/category-ads";
import type {
  CotAgeGuestBed,
  Guest,
  GuestDetails,
  WbeRowExtraBeds,
} from "../api/contracts/reservation";
import type { GuestTypesConfig } from "../api/contracts/site-config";

type CotAgeType = "infant" | "child";
type AgeType = Guest["ageType"];

/** What decides the bed a cot-age guest gets when nobody chooses one. */
export type CotDefaults = {
  guestTypes: GuestTypesConfig | null | undefined;
  cotMaxAge: number | null;
};

/**
 * A guest type's bed when nothing is chosen; mirrors the backend's `defaultChoiceFor`. A type
 * entirely inside the cot age shares a bed, one straddling it takes an extra bed.
 */
export const defaultCotAgeBed = (
  type: AgeType,
  { guestTypes, cotMaxAge }: CotDefaults,
): CotAgeGuestBed["bed"] | null => {
  const range = guestTypes?.[type];
  if (cotMaxAge === null || !range?.active || range.minAge > cotMaxAge) {
    return null;
  }
  return "maxAge" in range && range.maxAge <= cotMaxAge ? "none" : "extra";
};

/** An ad whose party needs extra beds the category cannot hold, or the site has too few of, is not for sale. */
export const isCategoryAdOffered = (
  ad: Pick<CategoryAdDetailed, "extraBeds">,
): boolean => {
  const beds = ad.extraBeds;
  if (!beds || beds.needed <= 0) return true;
  if (!beds.unitCanHold) return false;
  return beds.remaining === null || beds.remaining >= beds.needed;
};

/** Searched infants, then children, young enough for a cot; an infant given no age is taken to be. */
export const cotAgeGuestTypes = (
  guests: Pick<GuestDetails, "children" | "infants">,
  cotMaxAge: number | null,
): CotAgeType[] => {
  if (cotMaxAge === null) return [];
  const withinCotAge = (type: CotAgeType) => (guest: { age?: number }) =>
    (guest.age === undefined ? type === "infant" : guest.age <= cotMaxAge)
      ? [type]
      : [];
  return [
    ...guests.infants.flatMap(withinCotAge("infant")),
    ...guests.children.flatMap(withinCotAge("child")),
  ];
};

export const maxCotsForAd = (
  cot: CategoryAdCot | null,
  cotAgeCount: number,
): number =>
  cot
    ? Math.max(
        0,
        Math.min(
          cotAgeCount,
          cot.maxPerUnit ?? cotAgeCount,
          cot.remaining ?? cotAgeCount,
        ),
      )
    : 0;

/**
 * The cot choices a booking sends. Only cots are named: a guest without an entry keeps the
 * bed the party was priced with, which is what the ad promised.
 */
export const buildCotAgeGuests = (
  cotCount: number,
  guests: Pick<GuestDetails, "children" | "infants">,
  cot: CategoryAdCot | null,
): CotAgeGuestBed[] | undefined => {
  const eligible = cotAgeGuestTypes(guests, cot?.maxAge ?? null);
  const count = Math.min(cotCount, maxCotsForAd(cot, eligible.length));
  if (count <= 0) return undefined;
  return eligible.slice(0, count).map((ageType) => ({ ageType, bed: "cot" }));
};

/** A row's beds counted per kind, extra beds first; kinds the row has none of are left out. */
export const countRowBeds = (
  beds: WbeRowExtraBeds[] | undefined,
): { type: WbeRowExtraBeds["type"]; count: number }[] =>
  (["extra", "cot"] as const)
    .map((type) => ({
      type,
      count: (beds ?? [])
        .filter((bed) => bed.type === type)
        .reduce((sum, bed) => sum + bed.count, 0),
    }))
    .filter(({ count }) => count > 0);

/**
 * Offered, or offered once the party's own held room counts: a cart row in the category holds the
 * very unit and the very beds the search reports as taken, so swapping between that category's
 * ads must stay open. `heldExtraBeds` maps each held category to the extra beds its rows hold.
 */
export const isCategoryAdOfferedTo = (
  ad: Pick<CategoryAdDetailed, "extraBeds" | "categoryId">,
  heldExtraBeds: ReadonlyMap<string, number>,
): boolean => {
  const held = heldExtraBeds.get(ad.categoryId);
  if (held === undefined || !ad.extraBeds) return isCategoryAdOffered(ad);
  const { remaining } = ad.extraBeds;
  return isCategoryAdOffered({
    extraBeds: {
      ...ad.extraBeds,
      unitCanHold: true,
      remaining: remaining === null ? null : remaining + held,
    },
  });
};

/** The categories an ad list does not offer, for surfaces that show categories without their ads. */
export const hiddenCategoryIds = (
  ads: (Pick<CategoryAdDetailed, "extraBeds"> & { categoryId: string })[],
): Set<string> =>
  new Set(
    ads.filter((ad) => !isCategoryAdOffered(ad)).map((ad) => ad.categoryId),
  );

type PricedBeds = {
  count: number;
  pricePerNight: number | null;
  product?: BedProduct | null;
};

/** What a booking brings in beds, for the line next to its Välj. */
export type IncludedBeds = { extra: PricedBeds | null; cot: PricedBeds | null };

/** The party a unit is booked for, which a cot can take an extra bed away from. */
export type BedParty = {
  guests: GuestDetails;
  caps: CategoryExtraBedCapacity | null | undefined;
  guestTypes: GuestTypesConfig | null | undefined;
};

const partyGuests = (guests: GuestDetails): Pick<Guest, "ageType">[] => [
  ...Array.from({ length: guests.adults }, () => ({ ageType: "adult" as const })),
  ...Array.from({ length: guests.teenagers }, () => ({
    ageType: "teenager" as const,
  })),
  ...guests.children,
  ...guests.infants,
];

/**
 * The extra beds left once the chosen cots are taken. `needed` is planned with no cots; a guest in
 * a cot leaves the baseline count, so it can free a bed their type would otherwise be given.
 */
export const extraBedsAfterCots = (
  needed: number,
  cotCount: number,
  cot: CategoryAdCot | null | undefined,
  party: BedParty | undefined,
): number => {
  if (!party || needed <= 0 || cotCount <= 0) return needed;
  const cotDefaults = { guestTypes: party.guestTypes, cotMaxAge: cot?.maxAge ?? null };
  const enabled = {
    teenager: party.guestTypes?.teenager?.active !== false,
    child: party.guestTypes?.child?.active !== false,
    infant: party.guestTypes?.infant?.active !== false,
  };
  const everyone = partyGuests(party.guests);
  const outOfBed = [...everyone];
  for (const { ageType } of buildCotAgeGuests(cotCount, party.guests, cot ?? null) ?? []) {
    const taker = outOfBed.findIndex((guest) => guest.ageType === ageType);
    if (taker >= 0) outOfBed.splice(taker, 1);
  }
  const freed =
    extraBedNeed(party.caps, everyone, enabled, cotDefaults) -
    extraBedNeed(party.caps, outOfBed, enabled, cotDefaults);
  return Math.max(0, needed - freed);
};

/** The beds one or more units are booked with: the extra beds their party needs and the cots chosen. */
export const includedBeds = (
  units: {
    extraBeds: CategoryAdExtraBeds | null | undefined;
    cot: CategoryAdCot | null | undefined;
    cotCount: number;
    party?: BedParty;
  }[],
): IncludedBeds | null => {
  const total = (
    parts: {
      count: number;
      price: number | null | undefined;
      product: BedProduct | null | undefined;
    }[],
  ): PricedBeds | null => {
    const used = parts.filter((part) => part.count > 0);
    if (used.length === 0) return null;
    return {
      count: used.reduce((sum, part) => sum + part.count, 0),
      pricePerNight: used.find((part) => part.price)?.price ?? null,
      product: used[0].product ?? null,
    };
  };
  const extra = total(
    units.map((unit) => ({
      count: extraBedsAfterCots(
        unit.extraBeds?.needed ?? 0,
        unit.cotCount,
        unit.cot,
        unit.party,
      ),
      price: unit.extraBeds?.pricePerNight,
      product: unit.extraBeds?.product,
    })),
  );
  const cot = total(
    units.map((unit) => ({
      count: unit.cotCount,
      price: unit.cot?.pricePerNight,
      product: unit.cot?.product,
    })),
  );
  return extra || cot ? { extra, cot } : null;
};

/** A stored cot choice held to the party and the ad as they are now. */
export const chosenCotCount = (
  cotCount: number | undefined,
  guests: Pick<GuestDetails, "children" | "infants">,
  cot: CategoryAdCot | null | undefined,
): number => {
  const eligible = cotAgeGuestTypes(guests, cot?.maxAge ?? null).length;
  return Math.min(cotCount ?? 0, maxCotsForAd(cot ?? null, eligible));
};

/**
 * The extra beds a party needs in a category; mirrors the backend's `autoExtraBedNeed` over the
 * party left once guests whose type shares a bed by default are taken out.
 */
export const extraBedNeed = (
  caps: CategoryExtraBedCapacity | null | undefined,
  party: Pick<Guest, "ageType">[],
  enabledNonAdultTypes: Record<"teenager" | "child" | "infant", boolean>,
  cotDefaults: CotDefaults = { guestTypes: null, cotMaxAge: null },
): number => {
  if (!caps?.allowed || caps.maxGuestsWithout === null) return 0;
  const guests = party.filter(
    (guest) => defaultCotAgeBed(guest.ageType, cotDefaults) !== "none",
  );
  const surplus = (count: number, cap: number | null) =>
    cap === null ? 0 : Math.max(0, count - cap);
  const adults = guests.filter((g) => g.ageType === "adult").length;
  const nonAdults = guests.filter(
    (g) => g.ageType !== "adult" && enabledNonAdultTypes[g.ageType],
  ).length;
  const perTypeSurplus =
    surplus(adults, caps.maxAdultsWithout) +
    surplus(nonAdults, caps.maxChildrenWithout);
  return Math.max(perTypeSurplus, guests.length - caps.maxGuestsWithout, 0);
};
